/**
 * Deciding whether an event renders through the engine or the legacy TSX switch.
 *
 * There is no feature flag in the usual sense, because the two are not two ways
 * of drawing the same thing: a template is *either* a package (which only the
 * engine can render) or a TSX component (which the engine knows nothing about).
 * The shape of the stored config is the discriminator.
 *
 * The one deliberate exception is `?template-preview=<key>`, which renders a
 * bundled reference package over a real event. It exists so a package can be
 * checked against live data before anyone installs it, and it is inert in
 * production.
 */

import type { HostSurfaceConfig } from "@/lib/event-templates";
import { availablePackages, loadPackage } from "./loadPackage";
import { applyColorScheme, createTokenResolver } from "./tokens";
import type { TemplatePackage } from "./types";

/** The query parameter that previews a bundled package against a real event. */
export const PREVIEW_PARAM = "template-preview";

/**
 * A stored template config is a package when it carries the four parts the
 * renderer needs. A legacy config has `palette`/`typography` at the top level
 * and none of these, so the two can never be confused.
 */
export function isTemplatePackage(config: unknown): config is TemplatePackage {
  if (!config || typeof config !== "object") return false;
  const candidate = config as Record<string, unknown>;
  return (
    typeof candidate.manifest === "object" && candidate.manifest !== null &&
    typeof candidate.tokens === "object" && candidate.tokens !== null &&
    typeof candidate.layout === "object" && candidate.layout !== null &&
    Array.isArray(candidate.sections)
  );
}

export type ResolvedTemplate =
  | { kind: "package"; pkg: TemplatePackage; source: "installed" | "preview" }
  | { kind: "legacy" };

/**
 * Resolve how to render an event.
 *
 * @param config the event's stored template config
 * @param search `window.location.search`, for the preview override
 */
export function resolveTemplate(config: unknown, search = ""): ResolvedTemplate {
  const previewKey = new URLSearchParams(search).get(PREVIEW_PARAM);

  // Dev-only, and only for a package that ships in the repo — never a key that
  // arrives from the URL alone.
  if (previewKey) {
    const available = import.meta.env.DEV ? availablePackages() : [];

    if (available.includes(previewKey)) {
      try {
        return { kind: "package", pkg: loadPackage(previewKey), source: "preview" };
      } catch (error) {
        console.error(`[template-engine] preview package '${previewKey}' failed to load`, error);
      }
    } else if (import.meta.env.DEV) {
      // Say so loudly. Falling back to the TSX silently looks like the engine
      // rendering the package badly, which is a genuinely confusing bug to chase.
      console.warn(
        `[template-engine] '${previewKey}' is not an available package, falling back to the legacy template. ` +
        `Available: ${available.length ? available.join(", ") : "none — is server/resources/jtpk mounted into the dev server?"}`,
      );
    }
  }

  if (isTemplatePackage(config)) {
    return { kind: "package", pkg: config, source: "installed" };
  }

  return { kind: "legacy" };
}

/** True for a colour literal (hex or rgb/rgba), as opposed to a token reference. */
const isColorLiteral = (value: string): boolean => /^(#|rgba?\()/i.test(value.trim());

/**
 * The button style the package itself uses for the "open RSVP" call to action.
 *
 * The dialog's submit button continues the CTA that opened it, so it takes that
 * button's colours. The RSVP section is searched first (the dialog mounts there),
 * then every other section, then the shared fragments.
 */
export function rsvpButtonStyleOf(pkg: TemplatePackage): string | undefined {
  const find = (node: unknown): string | undefined => {
    if (Array.isArray(node)) {
      for (const child of node) {
        const found = find(child);
        if (found) return found;
      }
      return undefined;
    }
    if (!node || typeof node !== "object") return undefined;
    const record = node as Record<string, unknown>;
    if (record.t === "button" && record.action === "rsvp.open" && typeof record.style === "string") {
      return record.style;
    }
    for (const value of Object.values(record)) {
      const found = find(value);
      if (found) return found;
    }
    return undefined;
  };

  const rsvpSections = pkg.sections.filter((section) => section.type === "rsvp");
  const otherSections = pkg.sections.filter((section) => section.type !== "rsvp");
  return find(rsvpSections) ?? find(otherSections) ?? find(pkg.layout.fragments ?? {});
}

/**
 * A legacy-shaped config built from the package's own tokens.
 *
 * The host-owned dialogs (`PublicRsvpForm`, `PublicGiftContributionForm`) and the
 * lightbox predate the format and read an `EventTemplateConfig`. This bridges the
 * two so those surfaces sit inside the template's colours instead of defaulting.
 * Every field a host surface reads is filled, and every value is resolved to CSS:
 * package surfaces are token references such as `gradient.cardDiag`, which a
 * stylesheet cannot use. Nothing is borrowed from a built-in theme.
 *
 * | host field              | package source, first that resolves                                  |
 * |-------------------------|----------------------------------------------------------------------|
 * | palette.*               | tokens.palette.* (the eight host-contract colours, 1:1)              |
 * | typography.*            | tokens.typography.families.*, with the template.json font fallback   |
 * | surfaces.cardBackground | tokens.surfaces.cardBackground, else palette.surface                 |
 * | surfaces.cardBorder     | tokens.surfaces.cardBorder, else palette.line                        |
 * | surfaces.chipBackground | tokens.surfaces.chipBackground, else palette.accentSoft              |
 * | surfaces.chipText       | tokens.surfaces.chipText, else palette.text                          |
 * | surfaces.shadow         | tokens.surfaces.shadow, else none                                    |
 * | hero.buttonBg           | background of the RSVP CTA's button token, else palette.accent       |
 * | hero.buttonText         | text colour of the RSVP CTA's button token, else palette.background  |
 *
 * The owner's colour picker is applied first, so it reaches every row above
 * exactly where the package's `overriddenBy` declarations say it lands.
 */
export function paletteConfigFor(
  pkg: TemplatePackage,
  colorScheme?: string | null,
): HostSurfaceConfig {
  const tokens = applyColorScheme(pkg.tokens, colorScheme);
  const resolver = createTokenResolver(tokens, { fonts: pkg.manifest.fonts });
  const palette = tokens.palette as HostSurfaceConfig["palette"];

  // A solid colour: a literal, or a reference that resolves to one (a gradient
  // reference is rejected, since these values also feed contrast checks).
  const colorOf = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    const resolved = isColorLiteral(value) ? value : resolver.color(value);
    return resolved && isColorLiteral(resolved) ? resolved : undefined;
  };
  const backgroundOf = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    return isColorLiteral(value) ? value : resolver.background(value);
  };
  const shadowOf = (value: string | undefined): string | undefined =>
    value ? resolver.shadow(value) : undefined;

  const ctaStyle = rsvpButtonStyleOf(pkg);
  const ctaButton = ctaStyle ? tokens.buttons?.[ctaStyle] : undefined;

  return {
    palette,
    typography: {
      heading: resolver.fontFamily("heading"),
      body: resolver.fontFamily("body"),
      accent: resolver.fontFamily("accent"),
    },
    surfaces: {
      cardBackground: backgroundOf(tokens.surfaces.cardBackground) ?? palette.surface,
      cardBorder: colorOf(tokens.surfaces.cardBorder) ?? palette.line,
      chipBackground: colorOf(tokens.surfaces.chipBackground) ?? palette.accentSoft,
      chipText: colorOf(tokens.surfaces.chipText) ?? palette.text,
      shadow: shadowOf(tokens.surfaces.shadow) ?? "none",
    },
    hero: {
      buttonBg: colorOf(ctaButton?.background) ?? palette.accent,
      buttonText: colorOf(ctaButton?.color) ?? palette.background,
    },
  };
}
